import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/react';
import { NotebookPen } from 'lucide-react';
import { useRightPanel } from '../../contexts/RightPanelContext';
import { DossierEditor } from '../editor/DossierEditor';
import {
  listScratchpads, createScratchpad, updateScratchpad, parseContent, type Scratchpad,
} from '../../lib/scratchpads';
import './scratchpad-drawer.css';

const AUTOSAVE_MS = 600;

export function ScratchpadDrawer() {
  const { isRegistered, isOpen: panelOpen } = useRightPanel();
  const [open, setOpen] = useState(false);
  const [pad, setPad] = useState<Scratchpad | null>(null);

  // Ported verbatim from ScratchpadPage.tsx's autosave (final review: the
  // two files diverging - no debounce here, one PATCH per keystroke - is
  // exactly what caused this finding; do not reinvent this).
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingSave = useRef<{ id: string; content: string } | undefined>(undefined);

  // The one number the whole geometry hangs off.
  const rpWidth = isRegistered && panelOpen ? 'var(--color-right-panel-width)' : '0px';

  useEffect(() => {
    let cancelled = false;
    listScratchpads()
      .then(async (pads) => {
        if (cancelled) return;
        setPad(pads.length > 0 ? pads[0] : await createScratchpad({}));
      })
      .catch(() => { /* the tab still works; the pad loads on next open */ });
    return () => { cancelled = true; };
  }, []);

  const flushSave = useCallback(() => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = undefined; }
    const p = pendingSave.current;
    pendingSave.current = undefined;
    if (!p) return;
    updateScratchpad(p.id, { content: p.content })
      .then(() => {
        setPad((prev) => (prev && prev.id === p.id ? { ...prev, content: p.content } : prev));
      })
      .catch(() => { /* autosave retries on next edit */ });
  }, []);

  useEffect(() => () => { flushSave(); }, [flushSave]);

  const handleChange = (json: JSONContent) => {
    if (!pad) return;
    pendingSave.current = { id: pad.id, content: JSON.stringify(json) };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, AUTOSAVE_MS);
  };

  return (
    <>
      <aside
        className="scratchpad-drawer"
        data-testid="scratchpad-drawer"
        data-open={open ? 'true' : 'false'}
        style={{ ['--rp-w' as string]: rpWidth }}
        aria-hidden={!open}
        inert={!open}
      >
        {pad && (
          <>
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
              <span className="text-sm font-semibold text-text-primary">{pad.title}</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              <DossierEditor
                content={parseContent(pad.content)}
                onUpdate={handleChange} scratchpadId={pad.id}
              />
            </div>
          </>
        )}
      </aside>

      <button
        className="scratchpad-tab"
        style={{ ['--rp-w' as string]: rpWidth }}
        aria-expanded={open}
        aria-label="Scratchpad"
        onClick={() => setOpen((o) => !o)}
      >
        <NotebookPen size={13} />
      </button>
    </>
  );
}
